import { API_URL } from '../variables/constants';

/**
 * Compose une signature de courriel : le logo du bureau à gauche, le texte à droite.
 *
 * POURQUOI UN TABLEAU, ET NON DU FLEX. Les clients de messagerie ne sont pas des
 * navigateurs : Outlook rend le HTML avec le moteur de Word, qui ignore `flex`,
 * `grid` et la plupart des règles de mise en page modernes. Un tableau à deux
 * cellules est la seule disposition que tous rendent de la même façon depuis
 * vingt ans. Les attributs de présentation y sont écrits en dur, sur les balises,
 * pour la même raison : les feuilles de style externes ne survivent pas à l'envoi.
 *
 * POURQUOI L'ADRESSE DU LOGO ET NON `cid:`. Ici, la signature est affichée dans
 * l'application - fenêtre de rédaction, aperçu des paramètres -, où `cid:` ne
 * désigne rien. C'est le serveur qui, à l'envoi, remplace cette adresse par une
 * image jointe au message : servie par son adresse, elle resterait invisible chez
 * la plupart des destinataires, Gmail et Outlook masquant les images distantes.
 */

/**
 * Échappe une valeur destinée à être interpolée dans du HTML.
 *
 * POURQUOI, ALORS QUE CES VALEURS VIENNENT DE LA BASE. Le nom d'un agent et la
 * raison sociale d'un organisme sont saisis au back-office, pas par un inconnu -
 * mais « pas par un inconnu » n'est pas « pas du HTML ». La signature produite
 * ici est rendue par `dangerouslySetInnerHTML` dans l'aperçu ET part dans des
 * courriels : une raison sociale malencontreuse s'exécuterait chez tous les
 * agents du bureau, pas seulement chez celui qui l'a saisie.
 *
 * Un nom n'a jamais besoin de balises. Les échapper ne coûte rien et retire la
 * question.
 */
const echapper = (valeur) => String(valeur ?? '')
  .replace(/&/g, '&amp;')
  .replace(/</g, '&lt;')
  .replace(/>/g, '&gt;')
  .replace(/"/g, '&quot;')
  .replace(/'/g, '&#39;');

/** Adresse du logo d'un organisme, ou null s'il n'en a pas. */
export const urlLogoOrganisme = (organismeTrackingId, logo) => {
  if (!organismeTrackingId || !logo) return null;
  return `${API_URL}/v1/organismes/${organismeTrackingId}/branding/logo`;
};

/**
 * @param signatureHtml signature de l'agent, telle qu'il l'a rédigée
 * @param logoUrl       adresse du logo, ou null
 * @returns la signature inchangée s'il n'y a pas de logo - on ne fabrique pas une
 *          mise en page pour une cellule vide, qui laisserait un décalage inexpliqué
 */
export const signatureAvecLogo = (signatureHtml, logoUrl) => {
  const signature = signatureHtml || '';
  if (!logoUrl || !signature.trim()) return signature;

  return [
    '<table role="presentation" cellpadding="0" cellspacing="0" border="0" style="border-collapse:collapse;">',
    '<tr>',
    // `valign="top"` aligne le logo sur la première ligne du texte plutôt que sur
    // son milieu : une signature de quatre lignes ferait sinon flotter le logo.
    '<td valign="top" style="padding-right:12px;">',
    // `logoUrl` est construite par `urlLogoOrganisme` a partir d'API_URL et d'un
    // identifiant serveur ; l'echapper ferme la question d'une guillemet qui
    // s'echapperait de l'attribut, sans rien couter.
    `<img src="${echapper(logoUrl)}" alt="" width="72" style="display:block;border:0;width:72px;height:auto;">`,
    '</td>',
    `<td valign="top">${signature}</td>`,
    '</tr>',
    '</table>',
  ].join('');
};

/**
 * Signature proposée par défaut à un agent qui n'en a pas encore.
 *
 * POURQUOI UNE PAR DÉFAUT. Le champ s'ouvrait vide, et l'immense majorité des
 * agents le laissait vide : leurs messages partaient sans nom ni fonction, et
 * le destinataire - une compagnie, un bureau homologue - recevait un courriel
 * dont il ne savait pas qui l'écrivait. Proposer une signature déjà formée coûte
 * moins qu'espérer que chacun la rédige.
 *
 * ELLE N'EST QU'UNE PROPOSITION. Elle est déposée dans l'éditeur, pas
 * enregistrée : l'agent la corrige, la complète, ou l'efface. Une signature
 * écrite en base sans qu'il l'ait vue partirait dans ses messages à son insu.
 *
 * L'ORDRE DES LIGNES N'EST PAS ARBITRAIRE - nom, fonction, bureau, contact. Le
 * destinataire cherche d'abord à qui il parle, ensuite à quel titre, et ne
 * descend au téléphone que s'il veut rappeler.
 *
 * Le logo n'y figure pas : il est posé à gauche par `signatureAvecLogo`, qui
 * s'applique aussi bien aux signatures rédigées à la main.
 */
export const signatureParDefaut = (agent) => {
  if (!agent) return '';

  // Prénoms puis nom, sur UNE ligne : c'est ainsi qu'on se présente, et deux
  // lignes pour une identité feraient paraître la signature plus longue
  // qu'elle n'est.
  const identite = [agent.prenoms, agent.nom]
    .map((p) => (p || '').trim())
    .filter(Boolean)
    .join(' ');

  const lignes = [];
  if (identite) {
    lignes.push(`<div style="font-weight:700;">${echapper(identite)}</div>`);
  }
  if ((agent.profilNom || '').trim()) {
    lignes.push(`<div>${echapper(agent.profilNom.trim())}</div>`);
  }
  if ((agent.organismeRaisonSociale || '').trim()) {
    lignes.push(`<div>${echapper(agent.organismeRaisonSociale.trim())}</div>`);
  }

  // Le contact sur une seule ligne, séparé par un point médian : deux lignes
  // pour un téléphone et un courriel allongent la signature sans rien ajouter.
  // Chaque partie est échappée AVANT la jointure : échapper après transformerait
  // le point médian en entité, et le séparateur s'afficherait en clair.
  const contact = [agent.telephone, agent.email]
    .map((p) => (p || '').trim())
    .filter(Boolean)
    .map(echapper)
    .join(' · ');
  if (contact) {
    lignes.push(`<div style="color:#6b7280;">${contact}</div>`);
  }

  // Rien à signer : on rend une chaîne vide plutôt qu'un bloc de divs vides,
  // qui laisserait un espace inexpliqué en pied de message.
  if (lignes.length === 0) return '';

  return `<div style="font-size:13px;line-height:1.5;">${lignes.join('')}</div>`;
};
